import { ReactNode, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStandardRole, type StandardRole } from "@/hooks/useStandardRole";
import { useCanAccessModule, ModuleType } from "@/hooks/useModulePermissions";
import {
  usePrivilegedPageAccess,
  type PrivilegedAccessRequirement,
} from "@/hooks/usePrivilegedPageAccess";
import { ToothLoader } from "@/components/ToothLoader";
import { AccessDeniedMessage } from "@/components/shared/AccessDeniedMessage";
import { supabase } from "@/integrations/supabase/client";
import { useModuleFeatureAccess } from "@/hooks/useTenantFeatureAccess";
import { isTenantAccessEnabled, type TenantAccessInput } from "@/lib/tenant-access";

// Keep AppRole type for super_admin platform routes only
type AppRole = "super_admin" | "admin" | "staff";
type ModuleAction = "view" | "create" | "edit" | "delete";


interface ProtectedRouteProps {
  children: ReactNode;
  /** Module permission required to access this route */
  requiredModule?: ModuleType;
  /** Specific module action required. Defaults to view. */
  requiredModuleAction?: ModuleAction;
  /** App-level roles — only used for super_admin platform routes */
  requiredAppRoles?: AppRole[];
  /** Standard roles required (owner, manager, doctor, front_desk, assistant) */
  requiredStandardRoles?: StandardRole[];
  /** Owner/manager/accountant-only page gates for sensitive global pages */
  requiredPrivilegedAccess?: PrivilegedAccessRequirement;
  /** If true, user needs ANY of the specified roles (OR logic). Default is OR. */
  requireAny?: boolean;
  /** Custom module name for access denied message */
  moduleName?: string;
  /** Tenant feature (subscription/package) required — gates ALL users incl. owners */
  requiredFeature?: ModuleType;
}

export const ProtectedRoute = ({
  children,
  requiredModule,
  requiredModuleAction = "view",
  requiredAppRoles = [],
  requiredStandardRoles = [],
  requiredPrivilegedAccess,
  requireAny = true,
  moduleName,
  requiredFeature,
}: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  /** Permission configuration whose checks have resolved at least once. */
  const resolvedForRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const { data: standardRole, isLoading: standardRoleLoading } = useStandardRole();
  const modulePermission = useCanAccessModule(
    requiredModule || "cases" // Fallback module, won't be used if no module required
  );
  const privilegedAccess = usePrivilegedPageAccess();
  const featureGate = useModuleFeatureAccess(requiredFeature || "");

  // Determine what checks are needed
  const needsSuperAdminCheck = requiredAppRoles.includes("super_admin");
  const needsStandardRoleCheck = requiredStandardRoles.length > 0;
  const needsModuleCheck = !!requiredModule;
  const needsPrivilegedCheck = !!requiredPrivilegedAccess;
  const needsFeatureCheck = !!requiredFeature;
  const tenantAccess = standardRole?.tenantAccess;
  const tenantAccessDisabled = !!tenantAccess && tenantAccess.isEnabled === false && !standardRole?.isSuperAdmin;
  const hasRequiredModuleAccess = (() => {
    if (!needsModuleCheck) return true;
    switch (requiredModuleAction) {
      case "create":
        return modulePermission.canCreate;
      case "edit":
        return modulePermission.canEdit;
      case "delete":
        return modulePermission.canDelete;
      case "view":
      default:
        return modulePermission.canAccess;
    }
  })();

  useEffect(() => {
    if (!user?.id) return;

    const refetchTenantAccess = () => {
      queryClient.invalidateQueries({ queryKey: ["standard-role", user.id] });
    };

    window.addEventListener("focus", refetchTenantAccess);
    return () => window.removeEventListener("focus", refetchTenantAccess);
  }, [queryClient, user?.id]);

  useEffect(() => {
    if (!user?.id || !tenantAccess?.tenantId || standardRole?.isSuperAdmin) return;

    const channel = supabase
      .channel(`tenant-access:${tenantAccess.tenantId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "tenants",
          filter: `id=eq.${tenantAccess.tenantId}`,
        },
        (payload) => {
          const nextTenant = payload.new as TenantAccessInput;
          // Grace-aware: only tear down when access is actually revoked. A lapsed
          // subscription still inside its grace window keeps access, so it only
          // triggers the soft refetch below.
          if (!isTenantAccessEnabled(nextTenant)) {
            queryClient.invalidateQueries();
            supabase.getChannels().forEach((activeChannel) => {
              if (activeChannel.topic !== channel.topic) {
                supabase.removeChannel(activeChannel);
              }
            });
            return;
          }

          queryClient.invalidateQueries({ queryKey: ["standard-role", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, standardRole?.isSuperAdmin, tenantAccess?.tenantId, user?.id]);

  useEffect(() => {
    if (!tenantAccessDisabled) return;

    supabase.getChannels().forEach((channel) => {
      supabase.removeChannel(channel);
    });
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "standard-role",
    });
  }, [queryClient, tenantAccessDisabled]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <ToothLoader size="lg" />
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Show loading while checking roles/permissions — but ONLY before these checks
  // have ever resolved for this route.
  //
  // Swapping `children` for a loader unmounts the whole page and destroys its
  // React state. `useCanAccessModule.isLoading` is four queries ORed together
  // (useModulePermissions.tsx:224), so a background refetch of any one of them
  // used to wipe a half-written clinical visit — every field, every finding,
  // every vital — with no warning and nothing recoverable.
  //
  // Latching on the permission *configuration* (not just "once") means a route
  // change to a different module still blocks on its own first check, so this
  // never renders a page whose permissions have not been verified.
  const permissionKey = [
    requiredModule ?? "",
    requiredModuleAction,
    requiredFeature ?? "",
    [...requiredAppRoles].sort().join(","),
    [...requiredStandardRoles].sort().join(","),
    requiredPrivilegedAccess ?? "",
  ].join("|");

  const checksLoading =
    ((needsSuperAdminCheck || needsStandardRoleCheck) && standardRoleLoading) ||
    (needsModuleCheck && modulePermission.isLoading) ||
    (needsPrivilegedCheck && privilegedAccess.isLoading) ||
    (needsFeatureCheck && featureGate.isLoading);

  if (!checksLoading) resolvedForRef.current = permissionKey;

  if (checksLoading && resolvedForRef.current !== permissionKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <ToothLoader size="lg" />
      </div>
    );
  }

  // Super admin bypass — can access everything
  if (standardRole?.isSuperAdmin) {
    return <>{children}</>;
  }

  if (tenantAccessDisabled) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center px-4">
        <section className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">Access is disabled</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {tenantAccess?.tenantName
              ? `${tenantAccess.tenantName} is currently unavailable.`
              : "This tenant is currently unavailable."}
          </p>
          {tenantAccess?.reason && (
            <p className="mt-4 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              {tenantAccess.reason}
            </p>
          )}
          <p className="mt-4 text-sm text-muted-foreground">
            Please contact DentalFlow support to restore access.
          </p>
        </section>
      </main>
    );
  }

  // Tenant feature (subscription/package) gate — applies to everyone except super admins.
  if (needsFeatureCheck && !featureGate.hasModuleFeature) {
    return <AccessDeniedMessage moduleName={moduleName || "this feature"} />;
  }

  // If route requires super_admin and user is not, deny access
  if (needsSuperAdminCheck) {
    return <AccessDeniedMessage moduleName={moduleName || "this section"} />;
  }

  // Owner bypass — owners can access all tenant-level routes
  if (standardRole?.isOwner) {
    return <>{children}</>;
  }

  // Check standard role requirements
  if (needsStandardRoleCheck && standardRole) {
    const roleMap: Record<StandardRole, boolean> = {
      owner: standardRole.isOwner,
      manager: standardRole.isManager,
      doctor: standardRole.isDoctor,
      front_desk: standardRole.isFrontDesk,
      assistant: standardRole.isAssistant,
    };

    const hasRequiredStandardRoles = requiredStandardRoles.map(
      (role) => roleMap[role]
    );

    let hasAccess: boolean;
    if (requireAny) {
      hasAccess = hasRequiredStandardRoles.some(Boolean);
    } else {
      hasAccess = hasRequiredStandardRoles.every(Boolean);
    }

    if (!hasAccess) {
      return <AccessDeniedMessage moduleName={moduleName || "this section"} />;
    }
  }

  if (
    needsPrivilegedCheck &&
    !privilegedAccess.canAccessRequirement(requiredPrivilegedAccess)
  ) {
    return <AccessDeniedMessage moduleName={moduleName || "this section"} />;
  }

  // Check module permission requirements
  if (needsModuleCheck && !hasRequiredModuleAccess) {
    return <AccessDeniedMessage moduleName={moduleName || requiredModule} />;
  }

  return <>{children}</>;
};
